import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, CheckCircle2, AlertCircle, CircleSlash, Info, Loader2, X, Pencil } from "lucide-react";

/**
 * P3.2.2-b – Bestätigungsdialog für Typenschild-Vision (Frontend ONLY).
 *
 * b-1: Anzeige (Bild + Vorschläge + Ampel + Provenienz), ephemer.
 * b-2: EDITIERMODUS. „Werte bearbeiten" schaltet alle 8 Felder auf Eingabe um; Änderungen leben
 *      NUR im lokalen Dialog-State. „Abbrechen" verwirft, „Fertig" übernimmt Änderungen NUR in die
 *      Dialogansicht (NICHT in productFields, KEINE Übernahme). „Übernehmen" bleibt deaktiviert (b-3).
 * Vollständig ephemer: kein Persist, kein product_context, keine API, keine Generierung.
 */

// In den bestehenden product_context übernehmbare Felder (P3.1, eingefroren).
const PRIMARY_FIELDS = [
  ["manufacturer", "Hersteller"],
  ["model", "Modell"],
  ["article_number", "Artikelnummer"],
];
// Erkannt, aber (noch) NICHT Bestandteil der Generierung – rein informativ.
const TECHNICAL_FIELDS = [
  ["voltage", "Spannung"],
  ["power", "Leistung"],
  ["protection_class", "Schutzklasse"],
  ["ip_rating", "IP-Schutz"],
  ["serial_number", "Seriennummer"],
];
const ALL_FIELDS = [...PRIMARY_FIELDS, ...TECHNICAL_FIELDS];

function toDisplay(suggestions) {
  const out = {};
  for (const [key] of ALL_FIELDS) {
    const item = suggestions?.[key] || {};
    out[key] = { value: (item.value || "").trim(), confidence: item.confidence || "low" };
  }
  return out;
}

const ConfBadge = ({ value, confidence }) => {
  let Icon = CircleSlash;
  let color = "text-rose-500";
  if (value) {
    if (confidence === "high") {
      Icon = CheckCircle2;
      color = "text-emerald-600";
    } else if (confidence === "medium") {
      Icon = AlertCircle;
      color = "text-amber-500";
    } else {
      Icon = AlertCircle;
      color = "text-rose-500";
    }
  }
  return <Icon className={`w-4 h-4 shrink-0 ${color}`} />;
};

// Jedes Feld als kleine Karte (wiederverwendbar später für PDF/GTIN/Herstellerdatenbank).
const FieldCard = ({ name, label, item, technical }) => {
  const value = (item?.value || "").trim();
  const confidence = item?.confidence || "low";
  return (
    <div
      data-testid={`pe-field-${name}`}
      className="rounded-md border border-slate-200 bg-white px-3 py-2"
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <div className="flex items-center gap-2 mt-0.5">
        <span data-testid={`pe-conf-${name}`} data-confidence={value ? confidence : "none"}>
          <ConfBadge value={value} confidence={confidence} />
        </span>
        <span
          data-testid={`pe-value-${name}`}
          className={value ? "text-sm font-medium text-slate-900" : "text-sm italic text-slate-400"}
        >
          {value || "nicht erkannt"}
        </span>
      </div>
      {technical && value && (
        <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
          <Info className="w-3 h-3 shrink-0" />
          Noch nicht Bestandteil der Generierung
        </p>
      )}
    </div>
  );
};

const FieldCardEdit = ({ name, label, value, technical, onChange }) => (
  <div
    data-testid={`pe-field-${name}`}
    className="rounded-md border border-slate-200 bg-white px-3 py-2"
  >
    <label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</label>
    <Input
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      data-testid={`pe-input-${name}`}
      className="mt-1 h-9 border-slate-300"
    />
    {technical && (
      <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
        <Info className="w-3 h-3 shrink-0" />
        Noch nicht Bestandteil der Generierung
      </p>
    )}
  </div>
);

export const ProductEvidenceDialog = ({ open, loading, data, imageUrl, onClose, onApply }) => {
  const [mode, setMode] = useState("view");
  const [display, setDisplay] = useState(() => toDisplay(data?.suggestions));
  const [draft, setDraft] = useState({});

  // Neue Analyse ODER Schließen (data → null) → lokalen Zustand zurücksetzen (verwirft alle Edits).
  useEffect(() => {
    setDisplay(toDisplay(data?.suggestions));
    setMode("view");
    setDraft({});
  }, [data]);

  const notes = data?.source_notes || [];
  const provenance = data?.provenance || {};
  const model = provenance.model || "Gemini Vision";
  const editing = mode === "edit";

  const startEdit = () => {
    const d = {};
    for (const [key] of ALL_FIELDS) d[key] = display[key]?.value || "";
    setDraft(d);
    setMode("edit");
  };
  const cancelEdit = () => {
    setMode("view");
    setDraft({});
  };
  const finishEdit = () => {
    setDisplay((prev) => {
      const next = { ...prev };
      for (const [key] of ALL_FIELDS) {
        next[key] = { value: (draft[key] || "").trim(), confidence: prev[key]?.confidence || "low" };
      }
      return next;
    });
    setMode("view");
    setDraft({});
  };
  const setDraftField = (name, val) => setDraft((d) => ({ ...d, [name]: val }));

  const renderGroup = (fields) =>
    fields.map(([name, label]) =>
      editing ? (
        <FieldCardEdit
          key={name}
          name={name}
          label={label}
          value={draft[name] || ""}
          technical={TECHNICAL_FIELDS.some(([k]) => k === name)}
          onChange={setDraftField}
        />
      ) : (
        <FieldCard
          key={name}
          name={name}
          label={label}
          item={display[name]}
          technical={TECHNICAL_FIELDS.some(([k]) => k === name)}
        />
      )
    );

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="product-evidence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-slate-700" />
            {loading ? "Typenschild wird gelesen…" : editing ? "Werte bearbeiten" : "Typenschild erkannt"}
          </DialogTitle>
        </DialogHeader>

        {imageUrl && (
          <img
            src={imageUrl}
            alt="Hochgeladenes Typenschild"
            data-testid="pe-image-preview"
            className="w-full max-h-52 object-contain rounded-md border border-slate-200 bg-slate-50"
          />
        )}

        {loading ? (
          <div
            data-testid="pe-loading"
            className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500"
          >
            <Loader2 className="w-5 h-5 animate-spin" />
            Werte werden extrahiert…
          </div>
        ) : (
          <div className="max-h-[45vh] overflow-y-auto pr-0.5">
            {notes.length > 0 && (
              <div
                data-testid="pe-source-notes"
                className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"
              >
                {notes.join(" ")}
              </div>
            )}

            <div className="space-y-2">{renderGroup(PRIMARY_FIELDS)}</div>

            <div className="mt-3 pt-2 border-t border-slate-200">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">
                Weitere erkannte Angaben
              </p>
              <div className="space-y-2">{renderGroup(TECHNICAL_FIELDS)}</div>
            </div>

            <div
              data-testid="pe-provenance"
              className="mt-3 pt-2 border-t border-slate-200 text-[11px] text-slate-500 space-y-0.5"
            >
              <div>Quelle: Typenschildfoto</div>
              <div>Erkannt mit: {model}</div>
              <div className="text-slate-400">Status: Noch nicht bestätigt</div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {editing ? (
            <>
              <Button variant="ghost" onClick={cancelEdit} data-testid="pe-abbrechen">
                <X className="w-4 h-4 mr-1.5" />
                Abbrechen
              </Button>
              <Button onClick={finishEdit} data-testid="pe-fertig" className="bg-slate-900 hover:bg-slate-800">
                Fertig
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} data-testid="pe-verwerfen">
                <X className="w-4 h-4 mr-1.5" />
                Verwerfen
              </Button>
              <Button
                variant="outline"
                onClick={startEdit}
                disabled={loading || !data}
                data-testid="pe-bearbeiten"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Werte bearbeiten
              </Button>
              <Button
                onClick={() =>
                  onApply &&
                  onApply({
                    manufacturer: display.manufacturer?.value || "",
                    model: display.model?.value || "",
                    article_number: display.article_number?.value || "",
                  })
                }
                disabled={loading || !data}
                data-testid="pe-uebernehmen"
                className="bg-slate-900 hover:bg-slate-800"
              >
                Übernehmen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
